// React Hooks:
import React, { useState, useEffect } from "react";
// React-Router-Dom:
import { useNavigate } from "react-router-dom";
// Redux Toolkit Hooks:
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
// Redux Toolkit Actions & State:
import { headerHamburgerMenuAction, header } from "../../header/headerSlice";
import { logoutAction } from "../../../authentication/authenticationSlice";
// MUI Components:
import {
  Box,
  Container,
  Fade,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  useTheme,
} from "@mui/material";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// Icones:
import { ReactComponent as UserManagementIcon } from "../../../../assets/icons/user-management.svg";
import { ReactComponent as HomeIcon } from "../../../../assets/icons/home-icon.svg";
import { ReactComponent as TableIcon } from "../../../../assets/icons/table-icon.svg";
import { ReactComponent as KeyIcon } from "../../../../assets/icons/key2-icon.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close-icon.svg";
import { ReactComponent as LogoutIcon } from "../../../../assets/icons/logout-icon.svg";
import { getCookie } from "../../../../configs/cookie-helper/cookieHelper";

const useStyles = makeStyles((theme) => ({
  backdropStyle: {
    backdropFilter: "blur(2px)",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  sidebarBox: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    overflow: "hidden",
  },
  listItem: {
    "& span": {
      fontSize: "1rem",
    },
  },
  active: {
    color: `${theme.palette.text.primary} !important`,
    backgroundColor: `${theme.palette.secondary.light} !important`,
    "&:hover": {
      backgroundColor: `${theme.palette.secondary.dark} !important`,
    },
  },
}));

const menuItemUser = [
  {
    name: "Home",
    path: "/",
    icon: HomeIcon,
  },
  {
    name: "Reports",
    path: "/reports",
    icon: TableIcon,
  },
  {
    name: "Password",
    path: "/edit-password",
    icon: KeyIcon,
  },
];

const menuItemAdmin = [
  {
    name: "Home",
    path: "/",
    icon: HomeIcon,
  },
  {
    name: "Reports",
    path: "/reports",
    icon: TableIcon,
  },
  {
    name: "User Management",
    path: "/user-management",
    icon: UserManagementIcon,
  },
];

const ResponsiveSidebar = () => {
  const classes = useStyles();
  const theme = useTheme();

  const rule = getCookie("rule");

  const menuItem = rule === "1" ? menuItemAdmin : menuItemUser;

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    hamburgerMenu: { open },
  } = useAppSelector(header);

  const [active, setActive] = useState(0);

  const location = window.location.pathname;
  useEffect(() => {
    switch (location.split("/")[1]) {
      case "":
        setActive(0);
        break;
      case "reports":
        setActive(1);
        break;
      case "password":
        setActive(2);
        break;
      case "user-management":
        setActive(2);
        break;
      case "create-user":
        setActive(2);
        break;
      default:
        setActive(-1);
        break;
    }
  }, [location]);

  const activeHandler = (id: number) => {
    menuItem.map((item, index) => id === index && setActive(index));
  };

  return (
    <Container
      sx={{
        width: "100%",
        height: "100%",
        zIndex: 1200,
        position: "absolute",
        top: "0%",
        left: open ? "0%" : "-100%",
        transition: "all 0.4s",
        p: { xs: 0 },
        m: { xs: 0 },
        [theme.breakpoints.up("sm")]: {
          display: "none",
        },
      }}
      className={classes.backdropStyle}
      onClick={() => dispatch(headerHamburgerMenuAction(false))}
    >
      <Box
        sx={{
          width: "75%",
          height: "100%",
          position: "absolute",
          top: "0%",
          left: open ? "0%" : "-100%",
          transition: "all 0.8s",
          py: 6,
          px: 2,
          [theme.breakpoints.down(400)]: {
            width: "85%",
          },
        }}
        className={classes.sidebarBox}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            p: "16px 18px",
            width: "50px",
            top: "0%",
            left: "0%",
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            [theme.breakpoints.up("sm")]: {
              display: "none",
            },
          }}
          onClick={() => {
            dispatch(headerHamburgerMenuAction(false));
          }}
        >
          <Fade in={open === true} timeout={500}>
            <SvgIcon
              component={CloseIcon}
              inheritViewBox
              sx={{
                width: "16px",
                height: "16px",
                color: "black",
                cursor: "pointer",
              }}
            />
          </Fade>
        </Box>

        <List
          sx={{
            p: "0px",
          }}
        >
          {menuItem.map((item, index) => (
            <ListItem
              key={index}
              disablePadding
              sx={{
                display: "block",
              }}
              className={classes.listItem}
            >
              <ListItemButton
                sx={{
                  minHeight: "45px",
                  borderRadius: "5px",
                  justifyContent: "flex-start",
                  p: "12px 16px",
                  m: "0px 0px 4px 0px ",
                }}
                onClick={(event) => {
                  event.stopPropagation();

                  if (location !== item.path) {
                    activeHandler(index);
                    navigate(item.path);
                    dispatch(headerHamburgerMenuAction(false));
                  }
                }}
                className={active === index ? classes.active : ""}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: "8px",
                    justifyContent: "center",
                    color: "inherit",
                  }}
                >
                  <SvgIcon
                    component={item.icon}
                    inheritViewBox
                    sx={{
                      height: "16px",
                      color: "inherit",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  sx={{
                    m: 0,
                    "& span": {
                      lineHeight: "normal",
                      pt: "2px",
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            p: "12px 16px",
            width: "50px",
            bottom: "0%",
            left: "0%",
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            [theme.breakpoints.up("sm")]: {
              display: "none",
            },
          }}
          onClick={() => {
            dispatch(logoutAction());
            navigate("/login");
            dispatch(headerHamburgerMenuAction(false));
          }}
        >
          <Fade in={open === true} timeout={500}>
            <SvgIcon
              component={LogoutIcon}
              inheritViewBox
              sx={{
                width: "25px",
                color: "#DC4242",
                transform: "rotate(180deg)",
              }}
            />
          </Fade>
        </Box>
      </Box>
    </Container>
  );
};

export default ResponsiveSidebar;
